/**
 * Diagnostic (free readiness assessment) sizing + question sampling.
 *
 * Single source of truth for BOTH apps: the onboarding screens advertise
 * "{count} questions · ~{minutes} minutes" and "Covers all exam domains", so web
 * and mobile must build the test from the same numbers and the same sampler or
 * the promise silently drifts per platform (mobile shipped 10q/60s while web
 * still built 20q/90s — exactly the drift this module removes).
 */

import { fisherYates } from './fisherYates';

export const DIAGNOSTIC_TEST_QUESTION_COUNT = 10;
export const DIAGNOSTIC_TEST_SECONDS_PER_QUESTION = 60;

/**
 * Pick diagnostic questions from a pool, stratified across domains (topic_id).
 *
 * A plain shuffle-and-slice routinely returns zero questions from some domain
 * (10 uniform picks over a 7-domain pool miss a domain more often than not),
 * which breaks the "Covers all exam domains" promise and leaves that domain out
 * of the results screen's Domain Breakdown. Instead this groups the pool by
 * topic_id (String-normalized, matching questionPools' type-insensitive rule)
 * and draws round-robin — one question per domain, in random domain order,
 * repeating — so every domain with at least one pooled question is represented
 * whenever count >= domain count, and larger domains fill the remainder evenly.
 *
 * @param {Array}    pool       candidate questions: { topic_id, ... } (already
 *                              filtered to the diagnostic pool by the caller)
 * @param {number}   [count]    questions to pick (default DIAGNOSTIC_TEST_QUESTION_COUNT)
 * @param {Function} [shuffle]  (array) => array, injectable for deterministic tests
 * @returns {Array} picked questions in random order (never more than `count`)
 */
export function pickDiagnosticQuestions(
  pool,
  count = DIAGNOSTIC_TEST_QUESTION_COUNT,
  shuffle = fisherYates
) {
  const questions = (pool ?? []).filter(Boolean);
  if (questions.length <= count) return shuffle(questions);

  const byTopic = new Map();
  for (const q of questions) {
    const key = String(q?.topic_id ?? '');
    if (!byTopic.has(key)) byTopic.set(key, []);
    byTopic.get(key).push(q);
  }
  const buckets = shuffle([...byTopic.values()].map((bucket) => shuffle(bucket)));

  const picked = [];
  for (let round = 0; picked.length < count; round++) {
    let drew = false;
    for (const bucket of buckets) {
      if (picked.length >= count) break;
      if (round < bucket.length) {
        picked.push(bucket[round]);
        drew = true;
      }
    }
    if (!drew) break; // every bucket exhausted (unreachable while pool > count; defensive)
  }
  return shuffle(picked);
}
